import React, { useState } from "react";
import { useCourseFilter, useGroupBy, groupLabel } from "../hooks/useEventFilters";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPersonSwimming, faXmark, faChevronDown, faTrophy } from "@fortawesome/free-solid-svg-icons";
import { compareEvents, sortStrokeValues, parseEventName, collectStrokes, collectDistances } from "../../services/utils/EventOrdering";
import { EventFilterBar } from "./filters";
const SwimmerComparison = ({ swimmers, onClose, selectedStrokes = [], onSelectedStrokesChange, selectedDistances = [], onSelectedDistancesChange }) => {
    const [courseFilter] = useCourseFilter();
    const [collapsedCourses, setCollapsedCourses] = useState(new Set());
    const [groupBy, setGroupBy] = useGroupBy();
    const toggleCourse = (course) => {
        setCollapsedCourses(prev => {
            const next = new Set(prev);
            if (next.has(course))
                next.delete(course);
            else
                next.add(course);
            return next;
        });
    };
    //Get all unique events
    const allEvents = new Set();
    swimmers.forEach(swimmer => {
        swimmer.times.forEach(time => {
            const key = `${time.event}_${time.course}`;
            allEvents.add(key);
        });
    });
    //Get unique strokes and distances
    const allTimes = swimmers.flatMap(x => x.times);
    const strokes = collectStrokes(allTimes.map(t => t.event));
    const distances = collectDistances(allTimes.map(t => t.event));
    //Filter events
    const filteredEvents = Array.from(allEvents).filter(eventKey => {
        const [event, course] = eventKey.split('_');
        const { stroke, distance } = parseEventName(event);
        const courseMatch = courseFilter === 'all' || course === courseFilter;
        const strokeMatch = selectedStrokes.length === 0 || selectedStrokes.includes(stroke);
        const distanceMatch = selectedDistances.length === 0 || selectedDistances.includes(distance.toString());
        return courseMatch && strokeMatch && distanceMatch;
    }).sort((a, b) => compareEvents(a.split('_')[0], b.split('_')[0]));
    //Group events by course
    const groupedByCourse = filteredEvents.reduce((acc, eventKey) => {
        const [event, course] = eventKey.split('_');
        if (!acc[course]) {
            acc[course] = [];
        }
        acc[course].push(eventKey);
        return acc;
    }, {});
    const sortedCourses = Object.keys(groupedByCourse).sort((a, b) => {
        if (a === '50m')
            return -1;
        if (b === '50m')
            return 1;
        return a.localeCompare(b);
    });
    const groupedByStroke = filteredEvents.reduce((acc, eventKey) => {
        const [event] = eventKey.split('_');
        const { stroke } = parseEventName(event);
        if (!acc[stroke])
            acc[stroke] = [];
        acc[stroke].push(eventKey);
        return acc;
    }, {});
    const sortedStrokeGroups = sortStrokeValues(Object.keys(groupedByStroke));
    const activeGroups = groupBy === 'course' ? groupedByCourse : groupedByStroke;
    const activeGroupKeys = groupBy === 'course' ? sortedCourses : sortedStrokeGroups;
    //Helper to find swimmer and event
    const getTimeForSwimmer = (swimmer, eventKey) => {
        const [event, course] = eventKey.split('_');
        const time = swimmer.times.find(t => t.event === event && t.course === course);
        return time ? time.time : '-';
    };
    //Helper to parse time to seconds for comparison
    const parseTimeToSeconds = (timeStr) => {
        if (timeStr === '-' || !timeStr)
            return Infinity;
        const parts = timeStr.split(':');
        if (parts.length === 2) {
            return parseInt(parts[0]) * 60 + parseFloat(parts[1]);
        }
        return parseFloat(parts[0]);
    };
    //Helpter to determine fastest time
    const getFastestSwimmer = (eventKey) => {
        let fastestIndex = -1;
        let fastestTime = Infinity;
        swimmers.forEach((swimmer, index) => {
            const timeStr = getTimeForSwimmer(swimmer, eventKey);
            const seconds = parseTimeToSeconds(timeStr);
            if (seconds < fastestTime) {
                fastestTime = seconds;
                fastestIndex = index;
            }
        });
        return fastestIndex;
    };
    return (React.createElement("section", { className: "comparison-section-card" },
        React.createElement("div", { className: "section-header" },
            React.createElement("h2", null,
                "Comparing (",
                swimmers.length,
                " swimmers)"),
            React.createElement("button", { onClick: onClose, className: "btn-close btn-ghost" },
                React.createElement(FontAwesomeIcon, { icon: faXmark }),
                "Close")),
        React.createElement("div", { className: "swimmer-comparison-names" }, swimmers.map((swimmer, index) => (React.createElement("div", { key: swimmer.tiref, className: "comparison-swimmer-tag", style: { borderLeftColor: `hsl(${index * 120}, 70%,50%)` } },
            React.createElement("strong", null, swimmer.name),
            React.createElement("span", null,
                swimmer.birthYear ? `Born ${swimmer.birthYear}` : '',
                " \u2022 ",
                swimmer.gender || 'Unknown'))))),
        React.createElement(EventFilterBar, { strokes: strokes, selectedStrokes: selectedStrokes, onStrokesChange: onSelectedStrokesChange, distances: distances, selectedDistances: selectedDistances, onDistancesChange: onSelectedDistancesChange, groupBy: groupBy, onGroupByChange: setGroupBy }),
        filteredEvents.length === 0 ? (React.createElement("div", { className: "empty-state", style: { textAlign: 'center', padding: '2rem' } },
            React.createElement("p", null, "No events match the current filters"))) : (activeGroupKeys.map((groupKey, groupIndex) => (React.createElement("div", { key: groupKey, className: `club-group${groupIndex > 0 ? ' club-group-spaced' : ''}` },
            React.createElement("div", { className: "club-header", onClick: () => toggleCourse(groupKey) },
                React.createElement("h3", { className: "club-header-title" },
                    React.createElement(FontAwesomeIcon, { icon: faPersonSwimming }),
                    " ",
                    groupLabel(groupBy, groupKey)),
                React.createElement(FontAwesomeIcon, { icon: faChevronDown, className: `chevron-icon${!collapsedCourses.has(groupKey) ? ' expanded' : ''}` })),
            !collapsedCourses.has(groupKey) && React.createElement("div", { className: "table-container" },
                React.createElement("table", null,
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", null, "Event"),
                            React.createElement("th", { className: "hide-on-mobile" }, "Course"),
                            swimmers.map((swimmer, index) => (React.createElement("th", { key: swimmer.tiref, style: { color: `hsl(${index * 120}, 70%, 40%)` } }, swimmer.name))))),
                    React.createElement("tbody", null, activeGroups[groupKey].map(eventKey => {
                        const [event, course] = eventKey.split('_');
                        const fastestIndex = getFastestSwimmer(eventKey);
                        const rankedSwimmers = swimmers
                            .map((swimmer, index) => ({ swimmer, index, time: getTimeForSwimmer(swimmer, eventKey) }))
                            .sort((a, b) => parseTimeToSeconds(a.time) - parseTimeToSeconds(b.time));
                        return (React.createElement("tr", { key: eventKey },
                            React.createElement("td", { "data-label": "Event", className: "card-event-td" },
                                React.createElement("div", { className: "card-event-header" },
                                    React.createElement("strong", null,
                                        groupBy !== 'course' && (React.createElement(React.Fragment, null,
                                            React.createElement("span", { className: "course-pill" }, course === '50m' ? 'LC' : 'SC'),
                                            "\u00A0")),
                                        event)),
                                React.createElement("div", { className: "mobile-supplement comparison-rank-list" }, rankedSwimmers.map(({ swimmer, index, time }) => {
                                    const isFastest = index === fastestIndex && time !== '--';
                                    return (React.createElement("div", { key: swimmer.tiref, style: {
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '0.2rem 0.4rem',
                                            borderRadius: '4px',
                                            backgroundColor: isFastest ? 'rgba(5,150,105,0.12)' : 'transparent'
                                        } },
                                        React.createElement("span", { style: { color: `hsl(${index * 120}, 70%, 40%)`, fontWeight: isFastest ? 'bold' : 'normal' } },
                                            isFastest && (React.createElement(FontAwesomeIcon, { icon: faTrophy, style: { marginRight: '0.35rem', color: 'var(--success)' } })),
                                            swimmer.name),
                                        React.createElement("span", { style: { fontWeight: isFastest ? 'bold' : 'normal', color: time === '--' ? 'var(--gray-400)' : 'inherit' } }, time)));
                                }))),
                            React.createElement("td", { "data-label": "Course", className: "hide-on-mobile" }, course),
                            swimmers.map((swimmer, index) => {
                                const time = getTimeForSwimmer(swimmer, eventKey);
                                return (React.createElement("td", { key: swimmer.tiref, "data-label": swimmer.name, className: "hide-on-mobile", style: {
                                        fontWeight: index === fastestIndex && time !== '--' ? 'bold' : 'normal',
                                        color: time === '--' ? 'var(--gray-400)' : 'inherit'
                                    } }, time));
                            })));
                    }))))))))));
};
export default SwimmerComparison;
